// src/services/notificacionService.ts
import { axiosInstance } from './axiosConfig';
import { handleRequestWithAuth } from './refreshService';
import { operatorHeaders, requireOperatorToken } from './pos/operatorGrant';

const API_URL = 'notificaciones/';

// Interfaces relacionadas con Cita (simplificadas para la notificación)
export interface CitaNotificacion {
    id: number;
    codigo: string;
    estado: string;
    estado_ejecucion: string;
    fecha_inicio: string | null;
    fecha_fin: string | null;
    hora_inicio: string | null;
    hora_fin: string | null;
    cliente_info?: {
        id: number;
        first_name: string;
        last_name: string;
        email: string;
    } | null;
    servicio_info?: {
        id: number;
        nombre: string;
    } | null;
    sucursal_info?: {
        id: number;
        nombre: string;
    } | null;
    espacio_en_uso?: string;
    total?: number;
}

export interface Notificacion {
    id: number;
    mensaje: string;
    leida: boolean;
    fecha_creacion: string; // ISO datetime
    cita: CitaNotificacion;
}

// Obtener todas las notificaciones del usuario autenticado
export const fetchNotificaciones = async (): Promise<Notificacion[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(API_URL);
        return response.data;
    });
};

// Obtener una notificación por ID
export const fetchNotificacion = async (id: number): Promise<Notificacion> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(`${API_URL}${id}/`);
        return response.data;
    });
};

// Crear una nueva notificación (normalmente se genera automáticamente en el backend)
export const createNotificacion = async (data: {
    cita: number;
    mensaje: string;
    leida?: boolean;
}): Promise<Notificacion> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.post(API_URL, data);
        return response.data;
    });
};

// Actualizar una notificación (ej. marcar como leída)
export const updateNotificacion = async (
    id: number,
    data: { leida?: boolean; mensaje?: string }
): Promise<Notificacion> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.patch(`${API_URL}${id}/`, data);
        return response.data;
    });
};

// Eliminar una notificación
export const deleteNotificacion = async (id: number): Promise<void> => {
    return handleRequestWithAuth(async () => {
        await axiosInstance.delete(`${API_URL}${id}/`);
    });
};

/**
 * Confirmar una cita desde la notificación (acción personalizada).
 *
 * Es una mutación de la cita: el backend exige el grant del operador (PIN)
 * sobre la caja PERSISTIDA de la cita y atribuye la confirmación a ese
 * operador; sin grant responde 403, así que aquí no se llama.
 */
export const confirmarNotificacion = async (
    id: number,
    operadorToken: string | null | undefined,
): Promise<{ message: string; cita_id: number }> => {
    const config = operatorHeaders(requireOperatorToken(operadorToken));
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.post(`${API_URL}${id}/confirmar/`, {}, config);
        return response.data;
    });
};
